import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import superAdminApi from '../../utils/superAdminApi';
import toast from 'react-hot-toast';
import { FiArrowLeft, FiSave, FiUserPlus, FiTrash2, FiKey, FiEdit2, FiLock, FiUnlock } from 'react-icons/fi';

const TABS = [
  { key: 'info', label: 'Shop Info' },
  { key: 'users', label: 'Shop Users' },
  { key: 'settings', label: 'Settings' },
];

const emptyUserForm = { name: '', email: '', password: '', role: 'employee' };
const ROLE_LABEL = { admin: 'Admin', employee: 'Employee', cutter: 'Cutting' };

export default function ShopDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [tab, setTab] = useState('info');
  const [loading, setLoading] = useState(true);

  const [shop, setShop] = useState(null);
  const [savingShop, setSavingShop] = useState(false);

  const [users, setUsers] = useState([]);
  const [showUserModal, setShowUserModal] = useState(false);
  const [userForm, setUserForm] = useState(emptyUserForm);
  const [savingUser, setSavingUser] = useState(false);
  const [resetTarget, setResetTarget] = useState(null);
  const [resetPassword, setResetPassword] = useState('');
  const [editTarget, setEditTarget] = useState(null); // { _id, name, email, role }
  const [savingEdit, setSavingEdit] = useState(false);

  const [settings, setSettings] = useState(null);
  const [savingSettings, setSavingSettings] = useState(false);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [s, u, st] = await Promise.all([
        superAdminApi.get(`/shops/${id}`),
        superAdminApi.get('/shop-users', { params: { shopId: id } }),
        superAdminApi.get(`/settings/${id}`),
      ]);
      setShop(s.data.shop);
      setUsers(u.data.users);
      setSettings(st.data.settings);
    } catch {
      toast.error('Failed to load shop');
    }
    setLoading(false);
  }, [id]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // ── Shop Info ──
  const [translatingShop, setTranslatingShop] = useState(false);
  const setShopField = (field, val) => setShop(prev => ({ ...prev, [field]: val }));

  // Per-language name / address. `nameLabels`/`addressLabels` come from the
  // API; fall back to the legacy flat fields for a shop not yet re-saved.
  const nameL = () => ({ en: (shop.nameLabels?.en) || shop.name || '', hi: shop.nameLabels?.hi || '', ar: shop.nameLabels?.ar || '' });
  const addrL = () => ({ en: (shop.addressLabels?.en) || shop.address || '', hi: shop.addressLabels?.hi || '', ar: shop.addressLabels?.ar || '' });
  const setNameLang = (lang, v) => setShop(prev => ({ ...prev, nameLabels: { ...nameL(), [lang]: v } }));
  const setAddrLang = (lang, v) => setShop(prev => ({ ...prev, addressLabels: { ...addrL(), [lang]: v } }));

  // Fill only the BLANK language fields (name + address) via the translation
  // service. Anything already typed is kept; the admin edits before saving.
  const autoTranslateShop = async () => {
    setTranslatingShop(true);
    try {
      const [n, a] = await Promise.all([
        superAdminApi.post('/i18n/translate', { labels: nameL() }),
        superAdminApi.post('/i18n/translate', { labels: addrL() }),
      ]);
      const ns = n.data.labels || {}, as = a.data.labels || {};
      setShop(prev => ({
        ...prev,
        nameLabels: { en: nameL().en || ns.en || '', hi: nameL().hi || ns.hi || '', ar: nameL().ar || ns.ar || '' },
        addressLabels: { en: addrL().en || as.en || '', hi: addrL().hi || as.hi || '', ar: addrL().ar || as.ar || '' },
      }));
      if (n.data.provider === 'none') toast('Translation service off — fill manually', { icon: 'ℹ️' });
      else toast.success('Suggestions filled — review before saving');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Auto-translate failed');
    }
    setTranslatingShop(false);
  };

  const saveShop = async () => {
    setSavingShop(true);
    try {
      const r = await superAdminApi.put(`/shops/${id}`, {
        name: nameL().en, address: addrL().en,
        nameLabels: nameL(), addressLabels: addrL(),
        phone: shop.phone, email: shop.email, logo: shop.logo,
      });
      setShop(r.data.shop);
      toast.success('Shop info saved');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Save failed');
    }
    setSavingShop(false);
  };

  // ── Shop Users ──
  const addUser = async () => {
    if (!userForm.name || !userForm.email || !userForm.password) return toast.error('All fields are required');
    setSavingUser(true);
    try {
      await superAdminApi.post('/shop-users', { ...userForm, shopId: id });
      toast.success('Shop user created');
      setShowUserModal(false);
      setUserForm(emptyUserForm);
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to create user');
    }
    setSavingUser(false);
  };

  const toggleUser = async (u) => {
    const msg = u.isActive
      ? `Disable "${u.name}"?\n\nThis login is logged out right away and can't add, edit or delete anything until you enable it again. The shop admin can't re-enable it.`
      : `Enable "${u.name}" again?`;
    if (!window.confirm(msg)) return;
    try {
      await superAdminApi.put(`/shop-users/${u._id}/toggle`);
      toast.success(u.isActive ? 'Login disabled' : 'Login enabled');
      fetchAll();
    } catch { toast.error('Failed to update'); }
  };

  const saveEdit = async () => {
    if (!editTarget.name.trim() || !editTarget.email.trim()) return toast.error('Name and email are required');
    setSavingEdit(true);
    try {
      await superAdminApi.put(`/shop-users/${editTarget._id}`, {
        name: editTarget.name, email: editTarget.email, role: editTarget.role,
      });
      toast.success('Login updated');
      setEditTarget(null);
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Update failed');
    }
    setSavingEdit(false);
  };

  const removeUser = async (uid, name) => {
    if (!window.confirm(`Remove login "${name}"?`)) return;
    try {
      await superAdminApi.delete(`/shop-users/${uid}`);
      toast.success('Login removed');
      fetchAll();
    } catch { toast.error('Delete failed'); }
  };

  const submitReset = async () => {
    if (!resetPassword || resetPassword.length < 4) return toast.error('Password must be at least 4 characters');
    try {
      await superAdminApi.put(`/shop-users/${resetTarget._id}/reset-password`, { password: resetPassword });
      toast.success('Password reset');
      setResetTarget(null);
      setResetPassword('');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Reset failed');
    }
  };

  // ── Settings ──
  const setSettingsField = (field, val) => setSettings(prev => ({ ...prev, [field]: val }));
  const saveSettings = async () => {
    setSavingSettings(true);
    try {
      const r = await superAdminApi.put(`/settings/${id}`, settings);
      setSettings(r.data.settings);
      toast.success('Settings saved');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Save failed');
    }
    setSavingSettings(false);
  };

  if (loading || !shop) return <div className="loading"><div className="spinner"></div></div>;

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
        <Link to="/superadmin/shops" className="btn btn-outline btn-sm btn-icon"><FiArrowLeft size={14} /></Link>
        <h2 style={{ margin: 0, fontSize: 18 }}>{shop.name}</h2>
        {shop.isActive
          ? <span className="badge badge-success">Active</span>
          : <span className="badge badge-danger">Disabled</span>}
      </div>

      <div className="filter-tabs" style={{ marginBottom: 20 }}>
        {TABS.map(t => (
          <button key={t.key} className={`filter-tab ${tab === t.key ? 'active' : ''}`}
            onClick={() => setTab(t.key)}>{t.label}</button>
        ))}
      </div>

      {tab === 'info' && (
        <div className="card">
          <div className="card-header">
            <h3 className="card-title">🏪 Shop Information</h3>
            <button type="button" className="btn btn-outline btn-sm"
              onClick={autoTranslateShop} disabled={translatingShop}>
              {translatingShop ? 'Translating…' : '↻ Auto-translate name & address'}
            </button>
          </div>
          <div className="card-body">
            <div style={{ fontSize: 11, color: '#94a3b8', marginBottom: 10 }}>
              These show on the app header and printed bills in the language the user picks.
              Auto-translate only fills blank fields — what you type is never overwritten.
              A blank language falls back to English.
            </div>

            <div className="form-group">
              <label className="form-label">Shop Name — English</label>
              <input className="form-control" value={nameL().en}
                onChange={e => setNameLang('en', e.target.value)} />
              <label className="form-label" style={{ fontSize: 12, marginTop: 8 }}>Shop Name — العربية</label>
              <input className="form-control" dir="rtl" style={{ textAlign: 'right' }}
                placeholder="Arabic name" value={nameL().ar}
                onChange={e => setNameLang('ar', e.target.value)} />
            </div>

            <div className="form-group">
              <label className="form-label">Address — English</label>
              <textarea className="form-control" rows={2} value={addrL().en}
                onChange={e => setAddrLang('en', e.target.value)} />
              <label className="form-label" style={{ fontSize: 12, marginTop: 8 }}>Address — العربية</label>
              <textarea className="form-control" rows={2} dir="rtl" style={{ textAlign: 'right' }}
                placeholder="Arabic address" value={addrL().ar}
                onChange={e => setAddrLang('ar', e.target.value)} />
            </div>

            <div className="form-row-2">
              <div className="form-group">
                <label className="form-label">Phone</label>
                <input className="form-control" value={shop.phone || ''}
                  onChange={e => setShopField('phone', e.target.value)} />
              </div>
              <div className="form-group">
                <label className="form-label">Email</label>
                <input type="email" className="form-control" value={shop.email || ''}
                  onChange={e => setShopField('email', e.target.value)} />
              </div>
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <button className="btn btn-accent" onClick={saveShop} disabled={savingShop}>
                <FiSave size={15} /> {savingShop ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}

      {tab === 'users' && (
        <div className="card">
          <div className="card-header">
            <h3 className="card-title">👥 Shop Logins</h3>
            <button className="btn btn-accent btn-sm" onClick={() => setShowUserModal(true)}>
              <FiUserPlus size={14} /> Add Login
            </button>
          </div>
          <div className="table-container">
            <table>
              <thead>
                <tr><th>Name</th><th>Email</th><th>Role</th><th>Status</th><th>Actions</th></tr>
              </thead>
              <tbody>
                {users.length === 0 && (
                  <tr><td colSpan={5}>
                    <div className="empty-state">
                      <h3>No logins yet</h3>
                      <p>Create this shop's first admin login here</p>
                    </div>
                  </td></tr>
                )}
                {users.map(u => (
                  <tr key={u._id}>
                    <td style={{ fontWeight: 600 }}>{u.name}</td>
                    <td>{u.email}</td>
                    <td><span className="badge badge-gray">{ROLE_LABEL[u.role] || u.role}</span></td>
                    <td>
                      {u.isActive
                        ? <span className="badge badge-success">Active</span>
                        : <span className="badge badge-danger">Disabled</span>}
                    </td>
                    <td>
                      <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                        <button className="btn btn-outline btn-sm btn-icon" title="Edit name / email / role"
                          onClick={() => setEditTarget({ _id: u._id, name: u.name, email: u.email, role: u.role })}>
                          <FiEdit2 size={13} />
                        </button>
                        <button className="btn btn-outline btn-sm btn-icon" title="Reset Password"
                          onClick={() => setResetTarget(u)}>
                          <FiKey size={13} />
                        </button>
                        <button className="btn btn-outline btn-sm"
                          title={u.isActive ? 'Disable — blocks this login completely' : 'Enable this login again'}
                          style={u.isActive ? { color: '#b45309', borderColor: '#fde68a' } : { color: '#047857', borderColor: '#a7f3d0' }}
                          onClick={() => toggleUser(u)}>
                          {u.isActive ? <><FiLock size={13} /> Disable</> : <><FiUnlock size={13} /> Enable</>}
                        </button>
                        <button className="btn btn-outline btn-sm btn-icon" title="Remove"
                          style={{ color: '#ef4444', borderColor: '#fecaca' }}
                          onClick={() => removeUser(u._id, u.name)}>
                          <FiTrash2 size={13} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {tab === 'settings' && settings && (
        <div className="card">
          <div className="card-header"><h3 className="card-title">🧾 Billing Settings</h3></div>
          <div className="card-body">
            <div className="form-row-3">
              <div className="form-group">
                <label className="form-label">Currency</label>
                <input className="form-control" value={settings.currency || ''}
                  onChange={e => setSettingsField('currency', e.target.value)} />
              </div>
              <div className="form-group">
                <label className="form-label">VAT Percent (%)</label>
                <input type="number" className="form-control" value={settings.vatPercent || 0}
                  onChange={e => setSettingsField('vatPercent', e.target.value)} />
              </div>
              <div className="form-group">
                <label className="form-label">Thank You Message</label>
                <input className="form-control" value={settings.thankYouMessage || ''}
                  onChange={e => setSettingsField('thankYouMessage', e.target.value)} />
              </div>
            </div>
            <div className="form-row-3">
              <div className="form-group">
                <label className="form-label">Bill Number Format</label>
                <select className="form-control" value={settings.billNumberFormat || 'auto'}
                  onChange={e => setSettingsField('billNumberFormat', e.target.value)}>
                  <option value="auto">Auto-increment</option>
                  <option value="manual">Manual</option>
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Bill Number Prefix</label>
                <input className="form-control" placeholder="e.g. INV-" value={settings.billNumberPrefix || ''}
                  onChange={e => setSettingsField('billNumberPrefix', e.target.value)} />
              </div>
              <div className="form-group">
                <label className="form-label">Next Bill Number Counter</label>
                <input type="number" className="form-control" value={settings.billNumberCounter || 1000}
                  onChange={e => setSettingsField('billNumberCounter', e.target.value)} />
              </div>
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <button className="btn btn-accent" onClick={saveSettings} disabled={savingSettings}>
                <FiSave size={15} /> {savingSettings ? 'Saving...' : 'Save Settings'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add Shop User Modal */}
      {showUserModal && (
        <div className="modal-overlay" onClick={() => setShowUserModal(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3 className="modal-title">➕ Add Shop Login</h3>
              <button className="btn btn-outline btn-sm btn-icon" onClick={() => setShowUserModal(false)}>✕</button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label className="form-label">Full Name</label>
                <input className="form-control" value={userForm.name}
                  onChange={e => setUserForm({ ...userForm, name: e.target.value })} />
              </div>
              <div className="form-group">
                <label className="form-label">Email</label>
                <input type="email" className="form-control" value={userForm.email}
                  onChange={e => setUserForm({ ...userForm, email: e.target.value })} />
              </div>
              <div className="form-group">
                <label className="form-label">Password</label>
                <input type="password" className="form-control" placeholder="Min 4 characters" value={userForm.password}
                  onChange={e => setUserForm({ ...userForm, password: e.target.value })} />
              </div>
              <div className="form-group">
                <label className="form-label">Role</label>
                <select className="form-control" value={userForm.role}
                  onChange={e => setUserForm({ ...userForm, role: e.target.value })}>
                  <option value="employee">Employee</option>
                  <option value="admin">Admin</option>
                  <option value="cutter">Cutting (cutter — sees assigned orders only, no prices)</option>
                </select>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-outline" onClick={() => setShowUserModal(false)}>Cancel</button>
              <button className="btn btn-accent" onClick={addUser} disabled={savingUser}>
                {savingUser ? 'Creating...' : 'Create Login'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Login Modal */}
      {editTarget && (
        <div className="modal-overlay" onClick={() => setEditTarget(null)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3 className="modal-title">✏️ Edit Login</h3>
              <button className="btn btn-outline btn-sm btn-icon" onClick={() => setEditTarget(null)}>✕</button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label className="form-label">Full Name</label>
                <input className="form-control" value={editTarget.name}
                  onChange={e => setEditTarget({ ...editTarget, name: e.target.value })} />
              </div>
              <div className="form-group">
                <label className="form-label">Email (used to log in)</label>
                <input type="email" className="form-control" value={editTarget.email}
                  onChange={e => setEditTarget({ ...editTarget, email: e.target.value })} />
                <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 4 }}>
                  After saving, this person logs in with the new email. Password stays the same.
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">Role</label>
                <select className="form-control" value={editTarget.role}
                  onChange={e => setEditTarget({ ...editTarget, role: e.target.value })}>
                  <option value="employee">Employee</option>
                  <option value="admin">Admin</option>
                  <option value="cutter">Cutting (cutter)</option>
                </select>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-outline" onClick={() => setEditTarget(null)}>Cancel</button>
              <button className="btn btn-accent" onClick={saveEdit} disabled={savingEdit}>
                {savingEdit ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reset Password Modal */}
      {resetTarget && (
        <div className="modal-overlay" onClick={() => setResetTarget(null)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3 className="modal-title">🔑 Reset Password: {resetTarget.name}</h3>
              <button className="btn btn-outline btn-sm btn-icon" onClick={() => setResetTarget(null)}>✕</button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label className="form-label">New Password</label>
                <input type="password" className="form-control" placeholder="Min 4 characters" autoFocus
                  value={resetPassword} onChange={e => setResetPassword(e.target.value)} />
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-outline" onClick={() => setResetTarget(null)}>Cancel</button>
              <button className="btn btn-accent" onClick={submitReset}>Reset Password</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
